/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {
  VehicleScenarioSelectPopUpComponent
} from '../../../core/components/vehicle-scenario-select/vehicle-scenario-select-pop-up/vehicle-scenario-select-pop-up.component';
import {getDefaultYearRange} from '../../../core/helpers/generic-helpers';
import {StateService} from '../../../core/state/state.service';
import {GlobalScenario} from '../../../models/global-scenario-dto.model';
import {VehVolAnalysisReportRequest} from '../../../models/reports/veh-vol-analysis-report-request.model';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';

import {ReportService} from '../../../services/report.service';

@Component({
    selector: 'app-veh-vol-analysis-rpt',
    templateUrl: './veh-vol-analysis-rpt.component.html',
    styleUrl: './veh-vol-analysis-rpt.component.scss',
    standalone: false
})
export class VehVolAnalysisRptComponent implements OnInit {

  showSpinner: boolean;
  areScenariosSelected: boolean;
  aggregateTos: boolean;
  isGlobal: boolean;

  selectedReportType: string;
  selectedSegmentType: string;

  selectedWorkingScenario: VehicleScenario;
  selectedCompareScenario: VehicleScenario;
  selectedWorkingGlobalScenario: GlobalScenario;
  selectedCompareGlobalScenario: GlobalScenario;
  selectedMemberScenarios: VehicleScenario[];
  selectedCompareMemberScenarios: VehicleScenario[];

  yearRange: number[];
  startYear: number;
  endYear: number;

  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              public reportService: ReportService,
              public stateService: StateService,
              public dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.selectedReportType = 'WorkingVsCompare';
    this.selectedSegmentType = 'GSEG';
    this.aggregateTos = true;
    this.isGlobal = false;

    // If there is already a selected scenario within the state, then use that selected scenario


    if (this.stateService.isVehicleScenarioSelected) {
      this.selectedWorkingScenario = this.stateService.selectedScenario;
      this.selectedCompareScenario = this.stateService.selectedCompareScenario;
    }
    this.getYearRange();
    this.setScenariosSelected();
  }

  /**
   * Run the report and export to Excel
   */
  onRunReport(): void {
    // Validate that the run years are the correct values
    if (this.startYear > this.endYear) {
      this.snackBar.displayError('Start Year must be less than or equal to End Year', DISMISS_MSG);
    } else if (this.isGlobal && this.endYear - this.startYear > 10) {
      this.snackBar.displayError('Year range cannot exceed 10 years for Global Scenarios', DISMISS_MSG);
    } else {
      // If the report type is Year Over Year and the source is global,
      // then the compare global scenario should be the same as the working global scenario
      if (this.selectedReportType === 'YrOverYr' && this.isGlobal) {
        this.selectedCompareGlobalScenario = this.selectedWorkingGlobalScenario;
        this.selectedCompareMemberScenarios = this.selectedMemberScenarios;
      }
      // Run the report normally if no issues with the years
      const workingVehScenarioIds = this.isGlobal ? this.selectedWorkingGlobalScenario.id.toString()
        : this.selectedWorkingScenario.vehScenId.toString();

      const compareVehScenarioIds = this.isGlobal ? this.selectedCompareGlobalScenario.id.toString()
        : this.selectedCompareScenario.vehScenId.toString();

      const workingIndScenarioIds = this.isGlobal ? this.selectedMemberScenarios.map(scenario => scenario.industryScenario.id).join(',')
        : this.selectedWorkingScenario.industryScenario.id.toString();

      const compareIndScenarioIds = this.isGlobal ? this.selectedCompareMemberScenarios.map(scenario => scenario.industryScenario.id).join(',')
        : this.selectedCompareScenario.industryScenario.id.toString();

      // Some parameters change if the report type is Year over Year
      const isYearOverYear: boolean = this.selectedReportType === 'YrOverYr';
      const reportRequest: VehVolAnalysisReportRequest = {
        userId: this.stateService.getUserGmid(),
        workingVehScenarioIds: workingVehScenarioIds,
        compareVehScenarioIds: isYearOverYear ? workingVehScenarioIds : compareVehScenarioIds,
        workingIndScenarioIds: workingIndScenarioIds,
        compareIndScenarioIds: isYearOverYear ? workingIndScenarioIds : compareIndScenarioIds,
        workingScenName: this.isGlobal ? this.selectedWorkingGlobalScenario.globalScenarioName : this.selectedWorkingScenario.vehScenName,
        compareScenName: this.isGlobal ? this.selectedCompareGlobalScenario.globalScenarioName : this.selectedCompareScenario.vehScenName,
        workingIndScenName: this.isGlobal ? this.selectedMemberScenarios.map(scenario => scenario.industryScenario.industryScenarioName).join(',')
          : this.selectedWorkingScenario.industryScenario.industryScenarioName,
        compareIndScenName: this.isGlobal ? this.selectedCompareMemberScenarios.map(scenario => scenario.industryScenario.industryScenarioName).join(',')
          : this.selectedCompareScenario.industryScenario.industryScenarioName,
        reportType: this.selectedReportType,
        segmentType: this.selectedSegmentType,
        isGlobal: this.isGlobal,
        startYear: isYearOverYear ? this.startYear - 1 : this.startYear,
        endYear: isYearOverYear ? this.endYear + 1 : this.endYear,
        aggregateTos: this.aggregateTos
      };

      const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');

      const scenarioString = workingVehScenarioIds + (isYearOverYear ? '' : '-' + compareVehScenarioIds);
      const fileName = 'VehVolAnalysis_' + scenarioString + "_" + currDate;
      this.showSpinner = true;
      this.reportService.getVehVolAnalysisReport(reportRequest).subscribe({
        next: (response: any) => {
          this.excelService.exportToExcelFromServer(response, fileName);
        },
        error: (error: any) => {
          this.showSpinner = false;
          this.snackBar.displayError(error, DISMISS_MSG);
        },
        complete: () => {
          this.showSpinner = false;
          this.snackBar.displaySuccess('Downloaded Successfully', DISMISS_MSG);
        }
      });
    }

  }

  /**
   * Opens the pop-up to select a vehicle scenario
   */
  openVehSelectComponent = (): void => {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiselect: false,
        title: 'Select Vehicle Scenario',
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedWorkingScenario = res.selectedScenarios[0];
        this.selectedCompareScenario = this.selectedWorkingScenario;
        this.setScenariosSelected();
        // Automatically open the select compare scen pop up if the user is on the working vs compare rpt type
        if (this.selectedReportType === 'WorkingVsCompare') {
          this.selectCompareScenario();
        }
      }
    });
  }

  /**
   * Opens the SelectCompareScenario pop up component
   */
  selectCompareScenario(): void {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        validFcorgs: [this.selectedWorkingScenario.fcorg],
        title: 'Select Compare Scenario',
        isMultiselect: false,
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedCompareScenario = res.selectedScenarios[0];
        this.setScenariosSelected();
      }
    });
  }

  /**
   * Gets the year range for the reports
   */
  getYearRange(): void {
    const {yearRange, startYear, endYear} = getDefaultYearRange();
    this.yearRange = yearRange;
    this.startYear = startYear;
    this.endYear = endYear;
  }

  /**
   * Event handler for when a working global scenario is selected
   * @param data
   */
  onGlobalScenarioSelect(data: any): void {
    this.selectedWorkingGlobalScenario = data.globalScenario;
    this.selectedMemberScenarios = data.memberScenarios;
    this.setScenariosSelected();
  }


  /**
   * Event handler for when a compare global scenario is selected
   * @param data
   */
  onCompareGlobalScenarioSelect(data: any): void {
    this.selectedCompareGlobalScenario = data.globalScenario;
    this.selectedCompareMemberScenarios = data.memberScenarios;
    this.setScenariosSelected();
  }

  /**
   * Sets the boolean for whether both working and compare scenarios are selected. Used for disabling the Run Report button
   */
  setScenariosSelected(): void {
    if (this.isGlobal) {
      this.areScenariosSelected = this.selectedWorkingGlobalScenario !== undefined && this.selectedWorkingGlobalScenario !== null
        && this.selectedCompareGlobalScenario !== undefined && this.selectedCompareGlobalScenario !== null;

      // Year over Year doesn't need a compare scenario
      if (this.selectedReportType === 'YrOverYr') {
        this.areScenariosSelected = this.selectedWorkingGlobalScenario !== undefined && this.selectedWorkingGlobalScenario !== null;
      }
    } else {
      this.areScenariosSelected = this.selectedWorkingScenario !== undefined && this.selectedWorkingScenario !== null
        && this.selectedCompareScenario !== undefined && this.selectedCompareScenario !== null;
    }
  }

  /**
   * Resets the global scenarios
   */
  resetGlobalScenarios(): void {
    this.selectedWorkingGlobalScenario = undefined;
    this.selectedCompareGlobalScenario = undefined;
    this.selectedMemberScenarios = [];
    this.selectedCompareMemberScenarios = [];
    this.setScenariosSelected();
  }
}
